import { FC, useCallback, useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { FiltersDefinition, FiltersState } from '@factorialco/f0-react'
import {
  EmployeeForSelectV2Fragment,
  EmployeeSelectorV2AccessItemsDocument,
  EmployeeSelectorV2AccessItemsQuery,
  EmployeeSelectorV2Document,
  EmployeeSelectorV2ItemsDocument,
  EmployeeSelectorV2ItemsQuery,
  EmployeeSelectorV2Query,
  EmployeeSelectorV2QueryVariables,
} from 'generated/resources/graphql'

import { EntitySelectorV2 } from 'modules/core/components/EntitySelectorV2'
import { MultiSelectState } from 'modules/core/components/EntitySelectorV2/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeFilters } from 'modules/employees/lib/filters'

import { EmployeeSelectorV2Props } from './types'

export const EmployeeSelectorV2: FC<EmployeeSelectorV2Props> = ({
  affiliations = ['employee'],
  filters,
  forcedFilters,
  label,
  hint,
  status,
  messages,
  options,
  disabled,
  mandatory,
  children,
  useAccessId,
  ...props
}) => {
  const { employee, company } = useCurrentSession()

  const { filters: employeeDefaultFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()

  const mapExtraFiltersToVariables =
    'mapExtraFiltersToVariables' in props ? props.mapExtraFiltersToVariables : undefined

  const currentCompanyId = company.id
  const currentEmployeeId = employee?.id

  const extractConnection = useCallback(
    (data: EmployeeSelectorV2Query) => data.employees.employeeNamesConnection,
    []
  )

  const extractItems = useCallback(
    (data: EmployeeSelectorV2ItemsQuery | EmployeeSelectorV2AccessItemsQuery) =>
      data.employees.employeeNamesConnection?.nodes ?? [],
    []
  )

  const mapItemTitle = useCallback(
    (item: EmployeeForSelectV2Fragment) =>
      resolveEmployeeDisplayName(
        {
          fullName: item.fullName,
          lastName: item.lastName,
          preferredName: item.preferredName,
        },
        options?.displayNameStrategy
      ),
    [options?.displayNameStrategy]
  )

  const mapItemAvatar = useCallback(
    (item: EmployeeForSelectV2Fragment) => {
      if (options?.hideAvatars) {
        return undefined
      }

      return {
        deactivated: item.terminated ?? false,
        firstName: item.preferredName ?? item.firstName,
        lastName: item.lastName,
        src: buildUrl(item.access.avatar),
        type: 'person' as const,
      }
    },
    [options?.hideAvatars]
  )

  const mapItemValue = useCallback(
    (item: EmployeeForSelectV2Fragment) => (useAccessId ? item.access.id : item.id),
    [useAccessId]
  )

  const finalFilters = useMemo(() => {
    let res: Partial<typeof employeeDefaultFilters> & FiltersDefinition = {}

    const isFilterEmployeeDefaultFilter = (
      filter: string
    ): filter is keyof typeof employeeDefaultFilters => {
      return filter in employeeDefaultFilters
    }

    if (!filters || filters.find((f) => typeof f === 'string' && f.startsWith('!'))) {
      res = employeeDefaultFilters
    }

    filters?.forEach((filter) => {
      if (typeof filter === 'string' && filter.startsWith('!')) {
        delete res[filter.slice(1)]
      } else if (typeof filter === 'string' && isFilterEmployeeDefaultFilter(filter)) {
        res = { ...res, [filter]: employeeDefaultFilters[filter] }
      } else if (typeof filter !== 'string') {
        res = { ...res, ...filter }
      }
    })

    // Remove accessStatus filter when it's forced via forcedFilters
    if (forcedFilters?.accessStatus?.length) {
      delete res.accessStatus
    }

    return res
  }, [employeeDefaultFilters, filters, forcedFilters?.accessStatus])

  const mapFiltersToVariables = useCallback(
    ({
      forcedFilters: _forcedFilters,
      filters: _filters,
    }: {
      forcedFilters?: EmployeeSelectorV2Props['forcedFilters']
      filters?: FiltersState<typeof finalFilters>
    }): Omit<EmployeeSelectorV2QueryVariables, 'cursor' | 'perPage' | 'page'> => {
      let excludedIds = _forcedFilters?.excludeIds ? [..._forcedFilters.excludeIds] : undefined
      if (_forcedFilters?.excludeCurrentEmployee) {
        excludedIds = [...(excludedIds ?? []), currentEmployeeId]
      }

      let locationIds = _forcedFilters?.locationIds

      if (_filters?.workplace?.length) {
        locationIds = [...(locationIds ?? []), ..._filters.workplace]
      }

      let legalEntityIds = _forcedFilters?.legalEntityIds

      if (_filters?.legalEntity?.length) {
        legalEntityIds = [...(legalEntityIds ?? []), ..._filters.legalEntity]
      }

      let teamIds = _forcedFilters?.teamIds

      if (_filters?.team?.length) {
        teamIds = [...(teamIds ?? []), ..._filters.team]
      }

      let reportingTo = _forcedFilters?.reportingTo

      if (
        _filters?.reportingTo &&
        Array.isArray(_filters.reportingTo) &&
        _filters.reportingTo.every((item) => typeof item === 'number')
      ) {
        reportingTo = [...(reportingTo ?? []), ..._filters.reportingTo]
      }

      let timeoffManager = _forcedFilters?.timeoffManagerIds

      if (
        _filters?.timeoffManager &&
        Array.isArray(_filters.timeoffManager) &&
        _filters.timeoffManager.every((item) => typeof item === 'number')
      ) {
        timeoffManager = [...(timeoffManager ?? []), ..._filters.timeoffManager]
      }

      let jobLevelIds: number[] | undefined = undefined
      let jobCatalogTreeNodeIds: string[] | undefined = undefined

      if (_filters?.jobRole && Array.isArray(_filters.jobRole) && _filters.jobRole.length > 0) {
        const jobRoleValues = _filters.jobRole.map((id) =>
          typeof id === 'string' ? id : String(id)
        )
        jobLevelIds = !hasJobCatalogTreeFeature
          ? jobRoleValues.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
          : undefined
        jobCatalogTreeNodeIds = hasJobCatalogTreeFeature ? jobRoleValues : undefined
      }

      let accessStatus: string[] | undefined = undefined
      // Forced accessStatus takes precedence over filter value
      if (_forcedFilters?.accessStatus?.length) {
        accessStatus = _forcedFilters.accessStatus
      } else if (
        _filters?.accessStatus &&
        Array.isArray(_filters.accessStatus) &&
        _filters.accessStatus.every((item) => typeof item === 'string')
      ) {
        accessStatus = _filters.accessStatus
      }

      const filter = excludedIds ? { id: { notIn: excludedIds } } : {}

      const extraFilters = mapExtraFiltersToVariables?.({
        filters: _filters,
      })

      const variables: Omit<EmployeeSelectorV2QueryVariables, 'cursor' | 'perPage' | 'page'> = {
        companyId: _forcedFilters?.companyId ?? currentCompanyId,
        legalEntityIds,
        teamIds,
        locationIds,
        reportingTo,
        timeoffManager,
        accessStatus,
        onlyActive: !accessStatus?.length,
        referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
        referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
          ? jobCatalogTreeNodeIds
          : undefined,
        ids: _forcedFilters?.employeeIds,
        onlyMyReportees: _forcedFilters?.onlyMyReportees,
        withActiveContract: _forcedFilters?.withActiveContract,
        reporteesOrLed: _forcedFilters?.reporteesOrLed,
        withVerb: _forcedFilters?.withVerb,
        verb: _forcedFilters?.verb,
        ...extraFilters,
        affiliation: affiliations,
        filter: {
          ...filter,
          ...extraFilters?.filter,
        },
      }
      return variables
    },
    [
      affiliations,
      currentCompanyId,
      currentEmployeeId,
      mapExtraFiltersToVariables,
      hasJobCatalogTreeFeature,
    ]
  )

  const forcedExcludedIds = useMemo(() => {
    let excludedIds = forcedFilters?.excludeIds ? [...forcedFilters.excludeIds] : []
    if (forcedFilters?.excludeCurrentEmployee && currentEmployeeId) {
      excludedIds = [...excludedIds, currentEmployeeId]
    }
    return excludedIds.length > 0 ? excludedIds : undefined
  }, [forcedFilters?.excludeIds, forcedFilters?.excludeCurrentEmployee, currentEmployeeId])

  const finalLabel =
    label ?? i18n.tp('employees.select_employee', { count: props.multiple ? 2 : 1 })

  const component = (
    <EntitySelectorV2
      document={EmployeeSelectorV2Document}
      itemsDocument={
        useAccessId ? EmployeeSelectorV2AccessItemsDocument : EmployeeSelectorV2ItemsDocument
      }
      extractItems={extractItems}
      extractConnection={extractConnection}
      mapItemTitle={mapItemTitle}
      mapItemAvatar={mapItemAvatar}
      mapItemValue={mapItemValue}
      filters={finalFilters}
      forcedFilters={forcedFilters}
      mapFiltersToVariables={mapFiltersToVariables}
      options={options}
      disabled={disabled}
      label={finalLabel}
      hint={hint}
      status={status}
      messages={
        messages ?? {
          placeholder: i18n.t('employees.select_employee.other'),
        }
      }
      mandatory={mandatory}
      error={props.error}
      forcedExcludedIds={forcedExcludedIds}
      {...(props.multiple === true
        ? {
            multiple: true,
            value: props.value,
            onChange: (state: MultiSelectState<FiltersState<FiltersDefinition> | undefined>) => {
              props.onChange(state)
            },
          }
        : { value: props.value, onChange: props.onChange })}
    >
      {children}
    </EntitySelectorV2>
  )

  if (options?.asList) {
    return (
      <Box height="s400" flexDirection="row">
        {component}
      </Box>
    )
  }

  return component
}
